var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var CoerceoState_1;
import { MGPOptional, Utils } from '@everyboard/lib';
import { Coord } from '../../jscaip/Coord';
import { FourStatePiece } from '../../jscaip/FourStatePiece';
import { PlayerNumberTable } from '../../jscaip/PlayerNumberTable';
import { Vector } from '../../jscaip/Vector';
import { TriangularCheckerBoard } from '../../jscaip/state/TriangularCheckerBoard';
import { FourStatePieceTriangularGameState } from '../../jscaip/state/TriangularGameState';
import { Debug } from '../../utils/Debug';
import { CoerceoStep } from './CoerceoMove';
let CoerceoState = class CoerceoState extends FourStatePieceTriangularGameState {
    static { CoerceoState_1 = this; }
    tiles;
    captures;
    static NEIGHBORS_TILES_DIRECTIONS = [
        new Vector(+0, -2), // UP
        new Vector(+3, -1), // UP_RIGHT
        new Vector(+3, +1), // DOWN_RIGHT
        new Vector(+0, +2), // DOWN
        new Vector(-3, +1), // DOWN_LEFT
        new Vector(-3, -1), // UP_LEFT
    ];
    static getTilesUpperLeftCoord(tile) {
        const x = tile.x - (tile.x % 3);
        let y = tile.y;
        if (x % 2 === 0) {
            y -= (tile.y) % 2;
        }
        else {
            y -= (tile.y + 1) % 2;
        }
        return new Coord(x, y);
    }
    getPresentNeighborEntrances(tileUpperLeft) {
        return [
            new Coord(tileUpperLeft.x + 1, tileUpperLeft.y - 1), // UP
            new Coord(tileUpperLeft.x + 3, tileUpperLeft.y + 0), // UP-RIGHT
            new Coord(tileUpperLeft.x + 3, tileUpperLeft.y + 1), // DOWN-RIGHT
            new Coord(tileUpperLeft.x + 1, tileUpperLeft.y + 2), // DOWN
            new Coord(tileUpperLeft.x - 1, tileUpperLeft.y + 1), // DOWN-LEFT
            new Coord(tileUpperLeft.x - 1, tileUpperLeft.y + 0), // UP-LEFT
        ].filter((c) => this.isOnBoard(c));
    }
    constructor(board, turn, tiles, captures) {
        super(board, turn);
        this.tiles = tiles;
        this.captures = captures;
        tiles.makeImmutable();
        captures.makeImmutable();
    }
    applyLegalMovement(move) {
        const start = move.getStart();
        const landing = move.getEnd();
        const newBoard = this.getCopiedBoard();
        newBoard[landing.y][landing.x] = FourStatePiece.ofPlayer(this.getCurrentPlayer());
        newBoard[start.y][start.x] = FourStatePiece.EMPTY;
        return new CoerceoState_1(newBoard, this.turn, this.tiles, this.captures);
    }
    doMovementCaptures(move) {
        const capturedCoords = this.getCapturedNeighbors(move.getEnd());
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        let resultingState = this;
        for (const captured of capturedCoords) {
            resultingState = resultingState.capture(captured);
        }
        return resultingState;
    }
    getCapturedNeighbors(coord) {
        const opponent = this.getCurrentOpponent();
        const neighbors = TriangularCheckerBoard.getNeighbors(coord);
        return neighbors.filter((neighbor) => {
            if (this.isNotOnBoard(neighbor)) {
                return false;
            }
            if (this.getPieceAt(neighbor).is(opponent)) {
                return this.isSurrounded(neighbor);
            }
            return false;
        });
    }
    isSurrounded(coord) {
        const remainingFreedom = this.getEmptyNeighbors(coord, FourStatePiece.EMPTY);
        return remainingFreedom.length === 0;
    }
    capture(coord) {
        const newBoard = this.getCopiedBoard();
        const newCaptures = this.captures.getCopy();
        newBoard[coord.y][coord.x] = FourStatePiece.EMPTY;
        newCaptures.add(this.getCurrentPlayer(), 1);
        return new CoerceoState_1(newBoard, this.turn, this.tiles, newCaptures);
    }
    removeTilesIfNeeded(piece, countTiles) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        let resultingState = this;
        const currentTile = CoerceoState_1.getTilesUpperLeftCoord(piece);
        if (this.isTileEmpty(currentTile) &&
            this.isDeconnectable(currentTile)) {
            resultingState = this.deconnectTile(currentTile, countTiles);
            const neighbors = this.getPresentNeighborEntrances(currentTile);
            for (const neighbor of neighbors) {
                const spaceContent = resultingState.getPieceAt(neighbor);
                if (spaceContent === FourStatePiece.EMPTY) {
                    resultingState = resultingState.removeTilesIfNeeded(neighbor, countTiles);
                }
                else if (spaceContent.is(this.getCurrentOpponent()) &&
                    resultingState.isSurrounded(neighbor)) {
                    resultingState = resultingState.capture(neighbor);
                }
            }
        }
        return resultingState;
    }
    isTileEmpty(tileUpperLeft) {
        Utils.assert(this.getPieceAt(tileUpperLeft) !== FourStatePiece.UNREACHABLE, 'Should not call isTileEmpty on removed tile');
        for (let y = 0; y < 2; y++) {
            for (let x = 0; x < 3; x++) {
                const coord = tileUpperLeft.getNext(new Vector(x, y), 1);
                if (this.getPieceAt(coord) !== FourStatePiece.EMPTY) {
                    return false;
                }
            }
        }
        return true;
    }
    isDeconnectable(tile) {
        const neighborsIndex = this.getPresentNeighborTilesRelativeIndices(tile);
        if (neighborsIndex.length > 3) {
            return false;
        }
        let holeCount = 0;
        for (let i = 1; i < neighborsIndex.length; i++) {
            if (this.areNeighbor(neighborsIndex[i - 1], neighborsIndex[i]) === false) {
                holeCount += 1;
            }
        }
        if (this.areNeighbor(neighborsIndex[0], neighborsIndex[neighborsIndex.length - 1]) === false) {
            holeCount += 1;
        }
        return holeCount <= 1;
    }
    areNeighbor(smallTileIndex, bigTileIndex) {
        return smallTileIndex + 1 === bigTileIndex ||
            (smallTileIndex === 0 && bigTileIndex === 5);
    }
    getPresentNeighborTilesRelativeIndices(tile) {
        const neighborsIndices = [];
        let firstIndex = MGPOptional.empty();
        for (let i = 0; i < 6; i++) {
            const vector = CoerceoState_1.NEIGHBORS_TILES_DIRECTIONS[i];
            const neighborTile = tile.getNext(vector, 1);
            if (this.hasInequalPieceAt(neighborTile, FourStatePiece.UNREACHABLE)) {
                if (firstIndex.isAbsent()) {
                    firstIndex = MGPOptional.of(i);
                }
                neighborsIndices.push(i - firstIndex.get());
            }
        }
        return neighborsIndices;
    }
    deconnectTile(tileUpperLeft, countTiles) {
        const newBoard = this.getCopiedBoard();
        const x0 = tileUpperLeft.x;
        const y0 = tileUpperLeft.y;
        for (let y = 0; y < 2; y++) {
            for (let x = 0; x < 3; x++) {
                newBoard[y0 + y][x0 + x] = FourStatePiece.UNREACHABLE;
            }
        }
        const newTiles = this.tiles.getCopy();
        if (countTiles) {
            newTiles.add(this.getCurrentPlayer(), 1);
        }
        return new CoerceoState_1(newBoard, this.turn, newTiles, this.captures);
    }
    getLegalLandings(coord) {
        const legalLandings = [];
        for (const step of CoerceoStep.STEPS) {
            const landing = coord.getNext(step.direction, 1);
            if (this.hasPieceAt(landing, FourStatePiece.EMPTY)) {
                legalLandings.push(landing);
            }
        }
        return legalLandings;
    }
    getPiecesByFreedom() {
        const playersScores = PlayerNumberTable.of([0, 0, 0, 0], [0, 0, 0, 0]);
        for (const coordAndContent of this.getCoordsAndContents()) {
            const owner = coordAndContent.content.getPlayer();
            if (owner.isPlayer()) {
                const nbFreedom = this.getEmptyNeighbors(coordAndContent.coord, FourStatePiece.EMPTY).length;
                playersScores.add(owner, nbFreedom, 1);
            }
        }
        return playersScores;
    }
};
CoerceoState = CoerceoState_1 = __decorate([
    Debug.log
], CoerceoState);
export { CoerceoState };
